<script setup>
import { ref, watch } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  contact: {
    type: Object,
    required: true,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['save', 'change']);

// Form data
const formData = ref({ ...props.contact });

// Watch for external changes
watch(
  () => props.contact,
  newContact => {
    formData.value = { ...newContact };
  },
  { deep: true }
);

// Track changes
const handleChange = () => {
  emit('change');
};

// Save data
const handleSave = () => {
  emit('save', formData.value);
};

// Options for select fields
const genderOptions = [
  { value: '', label: 'Selecione' },
  { value: 'male', label: 'Masculino' },
  { value: 'female', label: 'Feminino' },
  { value: 'other', label: 'Outro' },
  { value: 'prefer_not_to_say', label: 'Prefiro não dizer' },
];

const maritalStatusOptions = [
  { value: '', label: 'Selecione' },
  { value: 'single', label: 'Solteiro(a)' },
  { value: 'married', label: 'Casado(a)' },
  { value: 'divorced', label: 'Divorciado(a)' },
  { value: 'widowed', label: 'Viúvo(a)' },
  { value: 'other', label: 'Outro' },
];

const leadStatusOptions = [
  { value: 'new', label: 'Novo' },
  { value: 'contacted', label: 'Contactado' },
  { value: 'qualified', label: 'Qualificado' },
  { value: 'proposal', label: 'Proposta' },
  { value: 'negotiation', label: 'Negociação' },
  { value: 'won', label: 'Ganho' },
  { value: 'lost', label: 'Perdido' },
];

const leadTemperatureOptions = [
  { value: 'cold', label: '❄️ Frio' },
  { value: 'warm', label: '🌡️ Morno' },
  { value: 'hot', label: '🔥 Quente' },
];

const leadSourceOptions = [
  { value: '', label: 'Selecione' },
  { value: 'website', label: 'Website' },
  { value: 'referral', label: 'Indicação' },
  { value: 'social_media', label: 'Redes Sociais' },
  { value: 'ads', label: 'Anúncios' },
  { value: 'email', label: 'Email' },
  { value: 'cold_call', label: 'Ligação Fria' },
  { value: 'event', label: 'Evento' },
  { value: 'partner', label: 'Parceiro' },
  { value: 'other', label: 'Outro' },
];

const contactChannelOptions = [
  { value: '', label: 'Selecione' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'phone', label: 'Telefone' },
  { value: 'email', label: 'Email' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'other', label: 'Outro' },
];

const dayOfWeekOptions = [
  { value: '', label: 'Selecione' },
  { value: 'monday', label: 'Segunda-feira' },
  { value: 'tuesday', label: 'Terça-feira' },
  { value: 'wednesday', label: 'Quarta-feira' },
  { value: 'thursday', label: 'Quinta-feira' },
  { value: 'friday', label: 'Sexta-feira' },
  { value: 'saturday', label: 'Sábado' },
  { value: 'sunday', label: 'Domingo' },
];
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Save Button -->
    <div class="flex justify-end">
      <Button
        :label="isSaving ? 'Salvando...' : 'Salvar Alterações'"
        :is-loading="isSaving"
        :disabled="isSaving"
        icon="i-lucide-save"
        @click="handleSave"
      />
    </div>

    <!-- Personal Information -->
    <section
      class="flex flex-col rounded-xl bg-n-solid-2 outline outline-1 outline-n-container overflow-hidden"
    >
      <div
        class="flex items-center gap-2 px-4 py-3 border-b border-n-weak bg-n-alpha-1"
      >
        <span class="i-lucide-user size-4 text-n-slate-11" />
        <h4 class="text-sm font-medium text-n-slate-12">
          Informações Pessoais
        </h4>
      </div>
      <div class="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">Nome</label>
          <input
            v-model="formData.name"
            type="text"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
            @input="handleChange"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">Nome do Meio</label>
          <input
            v-model="formData.middle_name"
            type="text"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
            @input="handleChange"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">Sobrenome</label>
          <input
            v-model="formData.last_name"
            type="text"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
            @input="handleChange"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">Email</label>
          <input
            v-model="formData.email"
            type="email"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
            @input="handleChange"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">Telefone</label>
          <input
            v-model="formData.phone_number"
            type="tel"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
            @input="handleChange"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">CPF/CNPJ</label>
          <input
            v-model="formData.cpf_cnpj"
            type="text"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
            @input="handleChange"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">Data de Nascimento</label>
          <input
            v-model="formData.birth_date"
            type="date"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
            @input="handleChange"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">Gênero</label>
          <select
            v-model="formData.gender"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
            @change="handleChange"
          >
            <option
              v-for="opt in genderOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">Estado Civil</label>
          <select
            v-model="formData.marital_status"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
            @change="handleChange"
          >
            <option
              v-for="opt in maritalStatusOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">Profissão</label>
          <input
            v-model="formData.profession"
            type="text"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
            @input="handleChange"
          />
        </div>
      </div>
    </section>

    <!-- Professional Information -->
    <section
      class="flex flex-col rounded-xl bg-n-solid-2 outline outline-1 outline-n-container overflow-hidden"
    >
      <div
        class="flex items-center gap-2 px-4 py-3 border-b border-n-weak bg-n-alpha-1"
      >
        <span class="i-lucide-briefcase size-4 text-n-slate-11" />
        <h4 class="text-sm font-medium text-n-slate-12">
          Informações Profissionais
        </h4>
      </div>
      <div class="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">Empresa</label>
          <input
            v-model="formData.company_name_text"
            type="text"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
            @input="handleChange"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">Cargo</label>
          <input
            v-model="formData.job_title"
            type="text"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
            @input="handleChange"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">Departamento</label>
          <input
            v-model="formData.department"
            type="text"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
            @input="handleChange"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">Tempo na Empresa</label>
          <input
            v-model="formData.time_at_company"
            type="text"
            placeholder="Ex: 2 anos"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:border-woot-500 focus:outline-none transition-colors"
            @input="handleChange"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">Salário Estimado (R$)</label>
          <input
            v-model="formData.estimated_salary"
            type="number"
            step="0.01"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
            @input="handleChange"
          />
        </div>
        <div class="flex items-end gap-6 pb-1">
          <label class="inline-flex items-center gap-2 cursor-pointer">
            <input
              v-model="formData.is_decision_maker"
              type="checkbox"
              class="size-4 rounded border-n-weak text-woot-500 focus:ring-woot-500 focus:ring-offset-0"
              @change="handleChange"
            />
            <span class="text-sm text-n-slate-12">Decisor</span>
          </label>
          <label class="inline-flex items-center gap-2 cursor-pointer">
            <input
              v-model="formData.is_influencer"
              type="checkbox"
              class="size-4 rounded border-n-weak text-woot-500 focus:ring-woot-500 focus:ring-offset-0"
              @change="handleChange"
            />
            <span class="text-sm text-n-slate-12">Influenciador</span>
          </label>
        </div>
      </div>
    </section>

    <!-- Lead Qualification -->
    <section
      class="flex flex-col rounded-xl bg-n-solid-2 outline outline-1 outline-n-container overflow-hidden"
    >
      <div
        class="flex items-center gap-2 px-4 py-3 border-b border-n-weak bg-n-alpha-1"
      >
        <span class="i-lucide-target size-4 text-n-slate-11" />
        <h4 class="text-sm font-medium text-n-slate-12">
          Qualificação do Lead
        </h4>
      </div>
      <div class="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">Lead Score (0-100)</label>
          <input
            v-model.number="formData.lead_score"
            type="number"
            min="0"
            max="100"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
            @input="handleChange"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">Status</label>
          <select
            v-model="formData.lead_status"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
            @change="handleChange"
          >
            <option
              v-for="opt in leadStatusOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">Temperatura</label>
          <select
            v-model="formData.lead_temperature"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
            @change="handleChange"
          >
            <option
              v-for="opt in leadTemperatureOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">Origem</label>
          <select
            v-model="formData.lead_source"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
            @change="handleChange"
          >
            <option
              v-for="opt in leadSourceOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">Nível de Interesse (1-5)</label>
          <input
            v-model.number="formData.interest_level"
            type="number"
            min="1"
            max="5"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
            @input="handleChange"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">Budget Estimado (R$)</label>
          <input
            v-model.number="formData.estimated_budget"
            type="number"
            step="0.01"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
            @input="handleChange"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">Timeline de Compra</label>
          <input
            v-model="formData.purchase_timeline"
            type="text"
            placeholder="Ex: 30 dias, Q1 2026"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:border-woot-500 focus:outline-none transition-colors"
            @input="handleChange"
          />
        </div>
      </div>
    </section>

    <!-- Contact Preferences -->
    <section
      class="flex flex-col rounded-xl bg-n-solid-2 outline outline-1 outline-n-container overflow-hidden"
    >
      <div
        class="flex items-center gap-2 px-4 py-3 border-b border-n-weak bg-n-alpha-1"
      >
        <span class="i-lucide-phone size-4 text-n-slate-11" />
        <h4 class="text-sm font-medium text-n-slate-12">
          Preferências de Contato
        </h4>
      </div>
      <div class="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">Telefone Secundário</label>
          <input
            v-model="formData.secondary_phone"
            type="tel"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
            @input="handleChange"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">WhatsApp</label>
          <input
            v-model="formData.whatsapp_number"
            type="tel"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
            @input="handleChange"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">Telegram</label>
          <input
            v-model="formData.telegram_username"
            type="text"
            placeholder="@username"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:border-woot-500 focus:outline-none transition-colors"
            @input="handleChange"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">Canal Preferido</label>
          <select
            v-model="formData.preferred_contact_channel"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
            @change="handleChange"
          >
            <option
              v-for="opt in contactChannelOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">Melhor Horário</label>
          <input
            v-model="formData.preferred_contact_time"
            type="text"
            placeholder="Ex: 9h-12h, tarde"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:border-woot-500 focus:outline-none transition-colors"
            @input="handleChange"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-11">Melhor Dia</label>
          <select
            v-model="formData.best_day_of_week"
            class="w-full h-9 px-3 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm focus:border-woot-500 focus:outline-none transition-colors"
            @change="handleChange"
          >
            <option
              v-for="opt in dayOfWeekOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>
      </div>
    </section>

    <!-- Personal Notes -->
    <section
      class="flex flex-col rounded-xl bg-n-solid-2 outline outline-1 outline-n-container overflow-hidden"
    >
      <div
        class="flex items-center gap-2 px-4 py-3 border-b border-n-weak bg-n-alpha-1"
      >
        <span class="i-lucide-sticky-note size-4 text-n-slate-11" />
        <h4 class="text-sm font-medium text-n-slate-12">
          Observações Pessoais
        </h4>
      </div>
      <div class="p-4">
        <textarea
          v-model="formData.personal_notes"
          rows="4"
          placeholder="Adicione observações importantes sobre este contato..."
          class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 text-sm placeholder:text-n-slate-10 focus:border-woot-500 focus:outline-none transition-colors resize-none"
          @input="handleChange"
        />
      </div>
    </section>
  </div>
</template>
